import { Flex, HorizontalTagInput, TagOption } from '@/components/core'
import { PolarityEmoji } from '@/pages/Mentions/components/Polarity/Polarity.component'
import { PreviewOpen } from '@icon-park/react'
import { BrandTerms, DataSourceType } from '../../types'
import { getPolarityIcon } from '../../utils/index'
import {
  CompoundPolarityTitle,
  PolarityContent,
  PolarityDivider,
  PolarityGroup,
  PolarityHeader,
  PolarityStatusSection,
  PolarityTermsSection,
  SmallPolarityEmoji,
  TermSection,
  TermsGrid,
  ViewRulesButton,
} from './PolarityRenderer.styles'

interface CompoundPolarityRendererProps {
  title: string
  expanded: boolean
  polarity: 'POSITIVO_FAVORAVEL' | 'NEGATIVO_DESFAVORAVEL'
  activeTab: DataSourceType
  termsState: BrandTerms
  onTermsChange: (updates: Partial<BrandTerms>) => void
  onPreviewClick: () => void
  tagValidator: (newTags: TagOption[], existingTags: TagOption[]) => TagOption[]
}

export function CompoundPolarityRenderer({
  title,
  expanded,
  polarity,
  activeTab,
  termsState,
  onTermsChange,
  onPreviewClick,
  tagValidator,
}: CompoundPolarityRendererProps): JSX.Element {
  function renderPolarityHeader(): JSX.Element {
    const getTitleEmojis = () => {
      if (title === 'POSITIVO / FAVORÁVEL') {
        return (
          <CompoundPolarityTitle>
            <PolarityGroup>
              <SmallPolarityEmoji>
                <PolarityEmoji value={getPolarityIcon('POSITIVO')} />
              </SmallPolarityEmoji>
              <span>POSITIVO</span>
            </PolarityGroup>
            <PolarityDivider>/</PolarityDivider>
            <PolarityGroup>
              <SmallPolarityEmoji>
                <PolarityEmoji value={getPolarityIcon('FAVORÁVEL')} />
              </SmallPolarityEmoji>
              <span>FAVORÁVEL</span>
            </PolarityGroup>
          </CompoundPolarityTitle>
        )
      } else {
        return (
          <CompoundPolarityTitle>
            <PolarityGroup>
              <SmallPolarityEmoji>
                <PolarityEmoji value={getPolarityIcon('NEGATIVO')} />
              </SmallPolarityEmoji>
              <span>NEGATIVO</span>
            </PolarityGroup>
            <PolarityDivider>/</PolarityDivider>
            <PolarityGroup>
              <SmallPolarityEmoji>
                <PolarityEmoji value={getPolarityIcon('DESFAVORÁVEL')} />
              </SmallPolarityEmoji>
              <span>DESFAVORÁVEL</span>
            </PolarityGroup>
          </CompoundPolarityTitle>
        )
      }
    }

    return (
      <PolarityHeader $expanded={expanded}>
        {getTitleEmojis()}
        <Flex $type="row" $align="center" $gap={8}>
          <span
            style={{
              fontSize: '12px',
              color: '#e53935',
              fontWeight: 600,
              marginRight: '12px',
            }}>
            opcional
          </span>
          {expanded && (
            <PolarityStatusSection>
              <ViewRulesButton onClick={onPreviewClick}>
                <PreviewOpen size={14} />
                Ver termos
              </ViewRulesButton>
            </PolarityStatusSection>
          )}
        </Flex>
      </PolarityHeader>
    )
  }

  function renderPolarityTermsSection(): JSX.Element {
    const isActiveTabSocial = activeTab === 'social'

    if (isActiveTabSocial) {
      const publishersTags =
        polarity === 'POSITIVO_FAVORAVEL' ?
          termsState.socialPositiveFavorablePublishersTags
        : termsState.socialNegativeUnfavorablePublishersTags

      const proprietaryTags =
        polarity === 'POSITIVO_FAVORAVEL' ?
          termsState.socialPositiveFavorableProprietaryTags
        : termsState.socialNegativeUnfavorableProprietaryTags

      return (
        <PolarityTermsSection>
          <TermsGrid>
            <TermSection>
              <HorizontalTagInput
                label="Publicadores"
                placeholder="Digite publicadores separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={(tags) =>
                  onTermsChange({
                    [`social${
                      polarity === 'POSITIVO_FAVORAVEL' ? 'PositiveFavorable' : 'NegativeUnfavorable'
                    }PublishersTags`]: tags,
                  })
                }
                preSelectedTags={publishersTags}
                validationConfig={{
                  maxCharacters: 200,
                  allowDuplicates: false,
                  customValidator: tagValidator,
                }}
                showClearButton={true}
              />
            </TermSection>
            <TermSection>
              <HorizontalTagInput
                label="Termos Proprietários"
                placeholder="Digite termos separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={(tags) =>
                  onTermsChange({
                    [`social${
                      polarity === 'POSITIVO_FAVORAVEL' ? 'PositiveFavorable' : 'NegativeUnfavorable'
                    }ProprietaryTags`]: tags,
                  })
                }
                preSelectedTags={proprietaryTags}
                validationConfig={{
                  maxCharacters: 200,
                  allowDuplicates: false,
                  customValidator: tagValidator,
                }}
                showClearButton={true}
              />
            </TermSection>
          </TermsGrid>
        </PolarityTermsSection>
      )
    } else {
      const channelsTags =
        polarity === 'POSITIVO_FAVORAVEL' ?
          termsState.pressPositiveFavorableChannelsTags
        : termsState.pressNegativeUnfavorableChannelsTags

      const proprietaryTags =
        polarity === 'POSITIVO_FAVORAVEL' ?
          termsState.pressPositiveFavorableProprietaryTags
        : termsState.pressNegativeUnfavorableProprietaryTags

      return (
        <PolarityTermsSection>
          <TermsGrid>
            <TermSection>
              <HorizontalTagInput
                label="Veículos"
                placeholder="Digite veículos separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={(tags) =>
                  onTermsChange({
                    [`press${
                      polarity === 'POSITIVO_FAVORAVEL' ? 'PositiveFavorable' : 'NegativeUnfavorable'
                    }ChannelsTags`]: tags,
                  })
                }
                preSelectedTags={channelsTags}
                validationConfig={{
                  maxCharacters: 200,
                  allowDuplicates: false,
                  customValidator: tagValidator,
                }}
                showClearButton={true}
              />
            </TermSection>
            <TermSection>
              <HorizontalTagInput
                label="Termos Proprietários"
                placeholder="Digite termos separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={(tags) =>
                  onTermsChange({
                    [`press${
                      polarity === 'POSITIVO_FAVORAVEL' ? 'PositiveFavorable' : 'NegativeUnfavorable'
                    }ProprietaryTags`]: tags,
                  })
                }
                preSelectedTags={proprietaryTags}
                validationConfig={{
                  maxCharacters: 200,
                  allowDuplicates: false,
                  customValidator: tagValidator,
                }}
                showClearButton={true}
              />
            </TermSection>
          </TermsGrid>
        </PolarityTermsSection>
      )
    }
  }

  if (!expanded) {
    return renderPolarityHeader()
  }

  return (
    <PolarityContent>
      {renderPolarityHeader()}
      {renderPolarityTermsSection()}
    </PolarityContent>
  )
}
